import { useState } from 'react';
import type { FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '@/components/ui/Modal';
import { faCircleQuestion, faPaperPlane, faSpinner } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showErrorToast, showSuccessToast } from '@/utils/toasts';

// Limits des Backends (api/handler/userQuestion.go)
const MAX_TITLE_LEN = 200;
const MAX_BODY_LEN = 10000;

const INPUT_CLASS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white';

function CharCounter({ length, max }: { length: number; max: number }) {
  const nearLimit = length >= max * 0.9;
  return (
    <span className={`text-xs tabular-nums ${nearLimit ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>
      {length.toLocaleString('de-DE')} / {max.toLocaleString('de-DE')}
    </span>
  );
}

interface AskQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Formular "Frage an das Team" als Modal. Der Entwurf liegt hier und nicht im Formular,
 * weil die Modal-Komponente ihre Kinder beim Schließen unmountet.
 */
export default function AskQuestionModal({ isOpen, onClose }: AskQuestionModalProps) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (trimmedTitle === '' || trimmedBody === '') {
      showErrorToast('Bitte gib einen Titel und deine Frage ein.');
      return;
    }

    try {
      setSubmitting(true);
      await APIRoutes.createUserQuestion({ title: trimmedTitle, body: trimmedBody });
      showSuccessToast('Deine Frage wurde gesendet.');
      setTitle('');
      setBody('');
      onClose();
    } catch (err) {
      handleAPIError(err, 'Deine Frage konnte nicht gesendet werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Frage stellen"
      icon={<FontAwesomeIcon icon={faCircleQuestion} className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
      maxWidth="2xl"
      closeDisabled={submitting}
      closeOnEscape
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-5">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Stelle deine Frage an das Team. Sie wird gesammelt und im Live-Call beantwortet.
        </p>

        <div>
          <div className="flex items-baseline justify-between mb-1">
            <label htmlFor="question-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Titel
            </label>
            <CharCounter length={title.length} max={MAX_TITLE_LEN} />
          </div>
          <input
            id="question-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={MAX_TITLE_LEN}
            placeholder="Worum geht es in einem Satz?"
            disabled={submitting}
            autoFocus
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-1">
            <label htmlFor="question-body" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Deine Frage
            </label>
            <CharCounter length={body.length} max={MAX_BODY_LEN} />
          </div>
          <textarea
            id="question-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={MAX_BODY_LEN}
            rows={8}
            placeholder="Beschreibe deine Frage so konkret wie möglich."
            disabled={submitting}
            className={INPUT_CLASS}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-md cursor-pointer disabled:cursor-not-allowed transition-colors"
          >
            <FontAwesomeIcon icon={submitting ? faSpinner : faPaperPlane} className={`w-4 h-4 ${submitting ? 'animate-spin' : ''}`} />
            Frage senden
          </button>
        </div>
      </form>
    </Modal>
  );
}
